import { ReactComponent as ArrowRight } from "app/assets/svg/arrow-right.svg"
import { ReactComponent as ArrowLeft } from "app/assets/svg/arrow-left.svg"
import { useEffect } from "react"
import { SubmitHandler } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslate } from "hooks/translate"
import {
  getFormData,
  getLeadResponseStatus
} from "state/preownedForm/selectors"
import { trackLeadThunk } from "state/preownedForm/slice"
import { PreOwnedFormStateData } from "state/preownedForm/types"
import { checkVATNumber } from "app/api"
import Form from "app/components/Form"
import Heading from "app/components/Heading"
import Description from "app/components/Description"
import Body from "app/components/Form/Body"
import InputField from "app/components/Form/Field/Input"
import SelectField from "app/components/Form/Field/Select"
import { StyledHeader } from "app/components/Form/Header/style"
import LanguageSwitch from "app/components/LanguageSwitch"
import {
  StyledFooter,
  StyledButtonLoadingIndicator,
  StyledButtonLoadingIndicatorWrapper
} from "app/components/Form/Footer/style"
import FormButton from "app/components/Form/Button"
import { Locales } from "i18n"
import { parseLinks } from "i18n/utils/parseLinks"
import { getAppLocale } from "state/app/selectors"
import { ActivityCategoryCodes } from "state/form/types"
import CheckboxField from "app/components/Form/Field/Checkbox"
import TurnstileField from "app/components/Form/Field/Turnstile"
import { useAppflow } from "app/hooks/useAppFlow"
import Button from "app/components/Button"
import { ClientEnum } from "../../types"
import { getTrackLeadPayload } from "../trackLeadPayload"
import { preOwnedSteps } from "./trackLeadPayload"

enum PreferredLanguages {
  NL = "nl",
  FR = "fr",
  EN = "en"
}

enum Locations {
  polestar_automotive_be = "polestar_automotive_be",
  zellik = "zellik",
  luik = "luik",
  mechelen = "mechelen",
  gent = "gent",
  hasselt = "hasselt",
  wijnegem = "wijnegem",
  zaventem = "zaventem",
  kortrijk = "kortrijk",
  "sint-genesius-rode" = "sint-genesius-rode"
}

const legalLinks: {
  [key in Locales]: {
    [key: string]: string
  }
} = {
  [Locales.NL]: {
    link: "https://www.polestar.com/nl-be/legal/privacy/privacy-policy/#marketing-via-e-mail-and-websites"
  },
  [Locales.FR]: {
    link: "https://www.polestar.com/fr-be/legal/privacy/privacy-policy/#marketing-via-e-mail-and-websites"
  }
}

export const PreOwned: React.FC = () => {
  const { clientType, carType, goBack } = useAppflow()
  const formData = useSelector(getFormData)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslate()
  const { isPending } = useSelector(getLeadResponseStatus)
  const locale = useSelector(getAppLocale)
  const { product, path } = useAppflow()

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    })
  }, [])

  const preferredLanguageOptions = Object.values(PreferredLanguages).map(
    locale => ({
      label: t(`system.locale.${locale}`),
      value: locale
    })
  )

  const locationOptions = Object.values(Locations).map(location => ({
    label: t(`location.${location}`),
    value: t(`location.${location}`)
  }))

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    })
  }, [])

  const handleFormSubmit: SubmitHandler<
    PreOwnedFormStateData
  > = async payload => {
    dispatch(
      trackLeadThunk(
        getTrackLeadPayload({
          step: 1,
          payload,
          config: preOwnedSteps,
          product: product!,
          carType: carType!,
          path,
          leadRecordType: clientType,
          journeyCompleted: true
        }) as any
      )
    ) // TODO: If you have some extra time feel free to fix the typing and remove the any
    navigate(`/preowned/success`)
  }

  const activityCategoryCodes = Object.entries(ActivityCategoryCodes).map(
    ([key, value]) => ({
      label: t(`system.industry.${key.toLowerCase()}`),
      value
    })
  )

  const activityCategoryCodePlaceholder = t("form.select.placeholder", {
    article: 1,
    option: t("system.sector").toLowerCase()
  })

  return (
    <>
      <StyledHeader>
        <LanguageSwitch />
      </StyledHeader>
      <Body>
        <Heading>{t("preownedform.heading")}</Heading>
        <Description>{t("preownedform.body")}</Description>
      </Body>
      <Form
        defaultValues={formData}
        onValid={handleFormSubmit}
        isPending={isPending}
      >
        <InputField
          name="firstName"
          label="system.first_name"
          rules={{ required: true }}
        />
        <InputField
          name="lastName"
          label="system.last_name"
          rules={{ required: true }}
        />
        <InputField name="zip" label="system.zip" rules={{ required: true }} />
        <InputField
          name="email"
          label="system.email"
          inputMode="email"
          type="email"
          rules={{
            required: true,
            pattern: {
              value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
              message: t("error.email")
            }
          }}
        />
        <InputField
          name="phone"
          label="system.phone"
          description={t("preownedform.field.phone.description")}
          type="tel"
          rules={{
            required: true,
            pattern: {
              value: /^\+32[^0][0-9]{6,}$/,
              message: t("error.phone")
            }
          }}
        />
        <SelectField
          name="language"
          label="preownedform.field.preferred_language.label"
          options={preferredLanguageOptions}
          rules={{ required: true }}
        />
        {clientType === ClientEnum.Business && (
          <>
            <InputField
              name="enterpriseNumber"
              label="system.enterprise_number"
              rules={{
                validate: {
                  enterprise_number: async enterpriseNumber =>
                    enterpriseNumber
                      ? await checkVATNumber(enterpriseNumber)
                      : true
                },
                required: true
              }}
              tooltip={t("tooltip.enterprise_number")}
              description={t("form.field.enterpriseNumber.description")}
            />
            <InputField
              name="enterpriseName"
              label="system.enterprise_name"
              rules={{ maxLength: 35, required: true }}
            />
            <SelectField
              name="activityCategoryCode"
              label="system.industry"
              options={activityCategoryCodes}
              placeholder={activityCategoryCodePlaceholder}
              rules={{ required: true }}
            />
          </>
        )}
        <InputField
          name="carModel"
          rules={{ required: true }}
          description={t("preownedform.field.car_model.description")}
          label="preownedform.field.car_model.label"
        />
        <InputField
          name="carModelType"
          rules={{ required: true }}
          description={t("preownedform.field.car_model_type.description")}
          label="preownedform.field.car_model_type.label"
        />
        <InputField
          name="carModelYear"
          rules={{ required: true }}
          label="preownedform.field.car_model_year.label"
        />
        <SelectField
          name="location"
          label="preownedform.field.location.label"
          options={locationOptions}
          rules={{ required: true }}
        />

        <CheckboxField
          name="validQuotationDisclaimer"
          label={t("system.legal.valid_quotation", {
            br: <br />
          })}
          rules={{ required: true }}
        />
        <CheckboxField
          name="informationNotice"
          label={t(
            "form.field.information_notice.label",
            parseLinks({
              link_info_notice: {
                target: "_blank",
                href: legalLinks[locale].link_info_notice
              }
            })
          )}
          rules={{ required: true }}
        />
        <CheckboxField
          name="privacyPolicy"
          label={t(
            "form.field.privacy_policy.label",
            parseLinks({
              link_privacy_policy: {
                target: "_blank",
                href: legalLinks[locale].link_privacy_policy
              },
              link_info_notice: {
                target: "_blank",
                href: legalLinks[locale].link_info_notice
              },
              link_privacy: {
                target: "_blank",
                href: legalLinks[locale].link_privacy
              }
            })
          )}
        />
        <TurnstileField
          lang={locale}
          name="turnstile"
          rules={{ required: true }}
        />

        <StyledFooter>
          <Button
            icon={ArrowLeft}
            iconPlacement="prefix"
            variant="plain"
            onClick={goBack}
          >
            {t("system.back")}
          </Button>
          <FormButton
            key={"button-next-preowned"}
            icon={ArrowRight}
            iconPlacement="postfix"
            action="next"
            disabled={isPending}
          >
            {isPending && (
              <StyledButtonLoadingIndicatorWrapper>
                <StyledButtonLoadingIndicator />
              </StyledButtonLoadingIndicatorWrapper>
            )}
            {t("preownedform.system.complete")}
          </FormButton>
        </StyledFooter>
      </Form>
    </>
  )
}
